import React, {useEffect, useRef, useState} from 'react';
import LandingSearchInput from '../LandingSearchInput';
import {Grid} from '@mui/material';
import {styled} from "@mui/material/styles";
import {useNavigate} from 'react-router';
import LandingNav from "../../../routing/LandingNav";
import {LandingGrid, LandingSubheader, LandingWhiteShadowHeader} from "./styles";
import {getQueryParamsFromPlace} from "../../../util/AddressUtils";
import {yardsAddress} from "../../../hooks/search/useLocationSearchParams";
import {saveGeocodedPlaceToLocalStorage} from "../../../util/PlaceStorageUtil";
import {PlacePrediction} from "../../placesAutocomplete/interfaces";
import {useMapsLibrary} from "@vis.gl/react-google-maps";

const LandingHeaderContainer = styled(Grid)(() => ({
  position: 'relative',
  top: 0,
  left: 0,
  right: 0,
  flexDirection:'column',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  justifyContent:'center',
  alignItems:'stretch'
}));

const LandingHeader = () => {
  const navigate = useNavigate();
  const [selectedPacItem, setSelectedPacItem] = useState<PlacePrediction | undefined>(); // update to use up/down events
  const geocodingLibrary = useMapsLibrary('geocoding');
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const isMountedRef = useRef(true);
  // Set while a suggestion is being opened. Enter runs the autocomplete's handler
  // (which opens the first suggestion) before the form submit, so the submit can
  // tell whether to fall back to the visitor's area (securspace-ui#3054).
  const openingPacRef = useRef(false);

  // Re-instantiate only when the geocoding library becomes available
  useEffect(() => {
    if (!geocodingLibrary) return;
    geocoderRef.current = new geocodingLibrary.Geocoder();
  }, [geocodingLibrary]);

  // Set isMountedRef to false on unmount to prevent state updates after unmount
  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  const redirectByPlacesAutocompleteItem = (pacItem: PlacePrediction) => {
    // need geo info to use the current search screen.
    if (!geocoderRef.current) return;
    openingPacRef.current = true;

    geocoderRef.current.geocode({placeId: pacItem.place_id}, (places, status) => {
      // Always check if component is still mounted before doing anything with the geocoding results to prevent memory leaks and errors from trying to update state after unmount
      if (!isMountedRef.current) return;

      // Check status first for better error visibility
      if (status !== google.maps.GeocoderStatus.OK) {
        openingPacRef.current = false;
        console.error('Geocoding failed in LandingHeader, status:', status, 'place_id:', pacItem.place_id);
        return;
      }

      if (!places || places.length === 0) {
        openingPacRef.current = false;
        console.error('No place results from geocode for place_id:', pacItem.place_id);
        return;
      }

      // 1. Get lat/lng of the place to save to local storage for later use in search screen
      // Assume first place is desired location because we searched by placeId
      const locationLatLng = places[0]?.geometry?.location;
      saveGeocodedPlaceToLocalStorage(places[0]);

      // 2. Open the place's market page (/yards/us/ga/savannah?…) if lat/lng exists, otherwise the visitor's area
      if (!locationLatLng) {
        navigate('/yards');
      } else {
        navigate(yardsAddress({...getQueryParamsFromPlace(places[0], 'short_name'), place_id: pacItem.place_id}));
      }
    });
  }

  // if they click it, it should redirect immediately
  const handleClickPac = (pacItem: PlacePrediction) => {
    redirectByPlacesAutocompleteItem(pacItem);
  }

  const handleSelectPac = (pacItem: PlacePrediction | undefined) => {
    setSelectedPacItem(pacItem);
  }

  const handleOnSubmit = () => {
    // Enter already opened a suggestion (the highlighted one, or the first). Opening
    // it again would geocode twice; navigating to the bare page would unmount the
    // header before it was geocoded ("Savannah" + Enter landed on the visitor's area).
    if (openingPacRef.current) return;
    if (selectedPacItem) {
      redirectByPlacesAutocompleteItem(selectedPacItem);
    } else {
      navigate('/yards');
    }
  }


  return <header>
    <LandingNav/>
    <LandingHeaderContainer container
          className={'h-100'}
    >
      <Grid item>
        <LandingWhiteShadowHeader variant={'h1'} align='center'>On-Demand Access to Yard
          Space</LandingWhiteShadowHeader>
      </Grid>
      <Grid item>
        <LandingSubheader variant={'h3'} align='center'>
          It's simple and secure
        </LandingSubheader>
      </Grid>
      <LandingGrid item></LandingGrid>
      <Grid item>
        <LandingSearchInput name={'pacSearch'}
                            selectedPacItem={selectedPacItem}
                            handleClickPac={handleClickPac}
                            handleSelectPac={handleSelectPac}
                            onSearchSubmit={handleOnSubmit}
        />
      </Grid>
    </LandingHeaderContainer>
  </header>
}

export default LandingHeader;
